import { Injectable, inject, PLATFORM_ID, signal } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';

export interface AccentPreset {
  key: string;
  name: string;
  light: string;
  dark: string;
}

export interface FontSizeOption {
  key: 'small' | 'medium' | 'large';
  label: string;
  value: string;
}

export interface WidthOption {
  key: 'narrow' | 'standard' | 'wide';
  label: string;
  value: string;
}

export interface StoredPrefs {
  accentKey: string;
  fontSize: 'small' | 'medium' | 'large';
  width: 'narrow' | 'standard' | 'wide';
}

const PREFS_KEY = 'stratum-reader-prefs';

const DEFAULT_PREFS: StoredPrefs = {
  accentKey: 'indigo',
  fontSize: 'medium',
  width: 'standard',
};

@Injectable({ providedIn: 'root' })
export class ThemeCustomizerService {
  private readonly _platformId = inject(PLATFORM_ID);

  readonly accentPresets: AccentPreset[] = [
    { key: 'indigo',  name: 'Indigo',  light: '#6366f1', dark: '#818cf8' },
    { key: 'sky',     name: 'Sky',     light: '#0ea5e9', dark: '#38bdf8' },
    { key: 'emerald', name: 'Emerald', light: '#10b981', dark: '#34d399' },
    { key: 'amber',   name: 'Amber',   light: '#f59e0b', dark: '#fbbf24' },
    { key: 'rose',    name: 'Rose',    light: '#f43f5e', dark: '#fb7185' },
    { key: 'violet',  name: 'Violet',  light: '#8b5cf6', dark: '#a78bfa' },
  ];

  readonly fontSizeOptions: FontSizeOption[] = [
    { key: 'small',  label: 'Small',  value: '0.9375rem' },
    { key: 'medium', label: 'Medium', value: '1.0625rem' },
    { key: 'large',  label: 'Large',  value: '1.1875rem' },
  ];

  readonly widthOptions: WidthOption[] = [
    { key: 'narrow',   label: 'Narrow',   value: '42rem' },
    { key: 'standard', label: 'Standard', value: '56rem' },
    { key: 'wide',     label: 'Wide',     value: '72rem' },
  ];

  /** Panel visibility */
  readonly isOpen = signal(false);

  /** Reactive dark-mode state — single source of truth for all components */
  readonly dark = signal(false);

  /** Call this immediately after any dark/light toggle to keep `dark` in sync */
  syncDark(): void {
    if (!isPlatformBrowser(this._platformId)) return;
    this.dark.set(document.documentElement.classList.contains('dark'));
  }

  /** Current persisted preferences (key-based) */
  readonly currentPrefs = signal<StoredPrefs>({ ...DEFAULT_PREFS });

  open(): void  { this.isOpen.set(true); }
  close(): void { this.isOpen.set(false); }
  toggle(): void { this.isOpen.update(v => !v); }

  isDarkMode(): boolean {
    if (!isPlatformBrowser(this._platformId)) return false;
    return document.documentElement.classList.contains('dark');
  }

  restore(): void {
    if (!isPlatformBrowser(this._platformId)) return;
    const stored = localStorage.getItem(PREFS_KEY);
    let prefs: StoredPrefs = { ...DEFAULT_PREFS };
    if (stored) {
      try {
        prefs = { ...DEFAULT_PREFS, ...JSON.parse(stored) };
      } catch { /* ignore malformed */ }
    }
    this.currentPrefs.set(prefs);
    this._applyPrefs(prefs);
    this.syncDark(); // initialise reactive dark signal

    // When no manual theme is stored, keep accent in sync as OS switches
    // between light and dark automatically (the ThemeService listener handles
    // the dark class; we only need to reapply the CSS colour variable here).
    window.matchMedia('(prefers-color-scheme: dark)').addEventListener('change', () => {
      // ThemeService listener has already toggled the class — sync signals unconditionally
      this.syncDark();
      this.reapplyAccent();
    });
  }

  setAccent(key: string): void {
    const preset = this.accentPresets.find(p => p.key === key);
    if (!preset) return;
    this.currentPrefs.update(p => ({ ...p, accentKey: key }));
    this._savePref('accentKey', key);
    if (isPlatformBrowser(this._platformId)) {
      const color = this.isDarkMode() ? preset.dark : preset.light;
      document.documentElement.style.setProperty('--reader-accent', color);
    }
  }

  setFontSize(key: 'small' | 'medium' | 'large'): void {
    const opt = this.fontSizeOptions.find(o => o.key === key);
    if (!opt) return;
    this.currentPrefs.update(p => ({ ...p, fontSize: key }));
    this._savePref('fontSize', key);
    if (isPlatformBrowser(this._platformId)) {
      document.documentElement.style.setProperty('--reader-font-size', opt.value);
    }
  }

  setWidth(key: 'narrow' | 'standard' | 'wide'): void {
    const opt = this.widthOptions.find(o => o.key === key);
    if (!opt) return;
    this.currentPrefs.update(p => ({ ...p, width: key }));
    this._savePref('width', key);
    if (isPlatformBrowser(this._platformId)) {
      document.documentElement.style.setProperty('--reader-content-width', opt.value);
    }
  }

  /**
   * Called after dark/light toggle so the accent CSS var is updated
   * to the correct variant of the active preset.
   */
  reapplyAccent(): void {
    if (!isPlatformBrowser(this._platformId)) return;
    this.syncDark(); // keep reactive signal in sync
    const preset = this.accentPresets.find(p => p.key === this.currentPrefs().accentKey)
      ?? this.accentPresets[0];
    const color = this.isDarkMode() ? preset.dark : preset.light;
    document.documentElement.style.setProperty('--reader-accent', color);
  }

  private _applyPrefs(prefs: StoredPrefs): void {
    const preset = this.accentPresets.find(p => p.key === prefs.accentKey) ?? this.accentPresets[0];
    const fOpt = this.fontSizeOptions.find(o => o.key === prefs.fontSize) ?? this.fontSizeOptions[1];
    const wOpt = this.widthOptions.find(o => o.key === prefs.width) ?? this.widthOptions[1];
    const color = this.isDarkMode() ? preset.dark : preset.light;
    document.documentElement.style.setProperty('--reader-accent', color);
    document.documentElement.style.setProperty('--reader-font-size', fOpt.value);
    document.documentElement.style.setProperty('--reader-content-width', wOpt.value);
  }

  private _savePref(key: keyof StoredPrefs, value: string): void {
    if (!isPlatformBrowser(this._platformId)) return;
    const stored = localStorage.getItem(PREFS_KEY);
    const current: StoredPrefs = stored
      ? { ...DEFAULT_PREFS, ...JSON.parse(stored) }
      : { ...DEFAULT_PREFS };
    (current as unknown as Record<string, string>)[key] = value;
    localStorage.setItem(PREFS_KEY, JSON.stringify(current));
  }
}
